import { AlertTriangle, CheckCircle2, BarChart3, MousePointer2, TrendingDown } from "lucide-react";

export interface TrackingEvent {
  type: string;
  selector?: string;
  text?: string;
  eventCount: number;
  isTracked: boolean;
}

export interface TrackingAuditData {
  totalEvents: number;
  trackedEvents: number;
  untrackedEvents: number;
  trackingCoverage: number;
  events: TrackingEvent[];
  recommendations?: string[];
}

type Props = {
  data: TrackingAuditData | null;
  loading?: boolean;
};

export function TrackingAuditDisplay({ data, loading }: Props) {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-24 bg-[color:hsl(var(--border)/0.3)] rounded-lg animate-pulse" />
        <div className="h-32 bg-[color:hsl(var(--border)/0.3)] rounded-lg animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-lg border border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface)/0.5)] p-6 text-center">
        <p className="text-[14px] text-[color:hsl(var(--muted-foreground))]">
          Tracking audit data not available
        </p>
      </div>
    );
  }

  const coveragePercent = Math.round(data.trackingCoverage * 100);
  const coverageStatus =
    coveragePercent >= 90 ? "excellent" : coveragePercent >= 70 ? "good" : "needs-improvement";

  return (
    <div className="space-y-6">
      {/* Coverage Summary */}
      <div className="rounded-lg border border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface)/0.5)] p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[13px] font-medium text-[color:hsl(var(--muted-foreground))] uppercase tracking-wider">
              Tracking Coverage
            </p>
            <p className="mt-3 text-5xl font-bold text-white tabular">{coveragePercent}%</p>
          </div>
          <div className="text-right">
            <p className="text-[13px] text-[color:hsl(var(--muted-foreground))]">
              {data.trackedEvents} of {data.totalEvents} events
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              {coverageStatus === "excellent" && (
                <>
                  <CheckCircle2 size={18} className="text-[color:hsl(var(--success))]" />
                  <span className="text-[13px] font-medium text-[color:hsl(var(--success))]">
                    Excellent tracking
                  </span>
                </>
              )}
              {coverageStatus === "good" && (
                <>
                  <CheckCircle2 size={18} className="text-[color:hsl(var(--primary))]" />
                  <span className="text-[13px] font-medium text-[color:hsl(var(--primary))]">
                    Good coverage
                  </span>
                </>
              )}
              {coverageStatus === "needs-improvement" && (
                <>
                  <AlertTriangle size={18} className="text-[color:hsl(var(--destructive))]" />
                  <span className="text-[13px] font-medium text-[color:hsl(var(--destructive))]">
                    Gaps found
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-6 h-2 w-full rounded-full bg-[color:hsl(var(--border))] overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              coverageStatus === "excellent"
                ? "bg-[color:hsl(var(--success))]"
                : coverageStatus === "good"
                  ? "bg-[color:hsl(var(--primary))]"
                  : "bg-[color:hsl(var(--destructive))]"
            }`}
            style={{ width: `${coveragePercent}%` }}
          />
        </div>
      </div>

      {/* Untracked Events Alert */}
      {data.untrackedEvents > 0 && (
        <div className="rounded-lg border border-[color:hsl(var(--destructive)/0.3)] bg-[color:hsl(var(--destructive)/0.1)] p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[color:hsl(var(--destructive))]" />
            <div className="flex-1">
              <p className="font-semibold text-white">
                {data.untrackedEvents} untracked interaction{data.untrackedEvents !== 1 ? "s" : ""}
              </p>
              <p className="mt-1 text-[13px] text-[color:hsl(var(--muted-foreground))]">
                These user actions aren't being measured. You're missing conversion signals that could inform your optimization strategy.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Event Breakdown */}
      <div>
        <p className="mb-4 text-[14px] font-semibold text-white">Event Breakdown</p>
        <div className="space-y-3">
          {data.events && data.events.length > 0 ? (
            data.events.map((event, idx) => (
              <EventRow key={idx} event={event} />
            ))
          ) : (
            <p className="text-[13px] text-[color:hsl(var(--muted-foreground))]">
              No events detected
            </p>
          )}
        </div>
      </div>

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <div className="rounded-lg border border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface)/0.5)] p-5">
          <p className="flex items-center gap-2 font-semibold text-white mb-4">
            <BarChart3 size={18} className="text-[color:hsl(var(--primary))]" />
            Tracking Recommendations
          </p>
          <ul className="space-y-2">
            {data.recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-3 text-[14px] text-[color:hsl(var(--foreground))]">
                <span className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-[color:hsl(var(--primary))]" />
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function EventRow({ event }: { event: TrackingEvent }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface)/0.3)] px-4 py-3">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="mt-1 shrink-0">
          {event.isTracked ? (
            <CheckCircle2 size={16} className="text-[color:hsl(var(--success))]" />
          ) : (
            <AlertTriangle size={16} className="text-[color:hsl(var(--destructive))]" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-white">
            {formatEventType(event.type)}
          </p>
          {event.text && (
            <p className="mt-1 text-[12px] text-[color:hsl(var(--muted-foreground))] truncate">
              "{event.text}"
            </p>
          )}
          {event.selector && (
            <p className="mt-1 text-[11px] text-[color:hsl(var(--subtle))] font-mono truncate">
              {event.selector}
            </p>
          )}
        </div>
      </div>
      <div className="ml-4 shrink-0 text-right">
        <p className="text-[13px] font-semibold text-white tabular">
          {event.eventCount}
        </p>
        <p className="text-[11px] text-[color:hsl(var(--muted-foreground))]">
          occurrence{event.eventCount !== 1 ? "s" : ""}
        </p>
      </div>
    </div>
  );
}

function formatEventType(type: string): string {
  const labels: Record<string, string> = {
    click: "Button Click",
    form_submit: "Form Submission",
    scroll: "Page Scroll",
    input: "Text Input",
    navigation: "Page Navigation",
    video_play: "Video Play",
    download: "File Download",
  };
  return labels[type] || type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
